/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { EmailAlign, EmailBlock, EmailContent } from "../types";
import { placeholderImage, STUB_PRODUCTS } from "../stub";
import { CharCounter, Field, Segmented, TemplateInput, TemplateTextarea } from "../shared";
import { blockLabel } from "./blocks";

type EmailInspectorProps = {
  email: EmailContent;
  block: EmailBlock | null;
  onEmailChange: (patch: Partial<EmailContent>) => void;
  onBlockChange: (block: EmailBlock) => void;
  onDeselect: () => void;
};

const STOCK_IMAGES = [
  placeholderImage("Autumn menu", "#e76f51", "#f4a261"),
  placeholderImage("Brunch", "#264653", "#2a9d8f"),
  placeholderImage("Happy birthday", "#7b2cbf", "#e0aaff"),
  placeholderImage("New season", "#386641", "#a7c957"),
];

export const EmailInspector = defineComponent<EmailInspectorProps>({
  name: "EmailInspector",
  props: ["email", "block", "onEmailChange", "onBlockChange", "onDeselect"],
  setup(props) {
    const { t } = useLocalization();

    // Typed patch for the currently selected block.
    const set = <B extends EmailBlock>(block: B, patch: Partial<B>) => props.onBlockChange({ ...block, ...patch });

    const AlignPicker = (value: EmailAlign, onChange: (v: EmailAlign) => void) => (
      <Field label={t("campaigns.studio_email.align")}>
        <Segmented
          size="xs"
          value={value}
          options={[
            { value: "left", label: "", icon: "format_align_left" },
            { value: "center", label: "", icon: "format_align_center" },
            { value: "right", label: "", icon: "format_align_right" },
          ]}
          onChange={(v) => onChange(v as EmailAlign)}
        />
      </Field>
    );

    const TextInput = (label: string, value: string, onChange: (v: string) => void, placeholder?: string) => (
      <Field label={label}>
        <input
          type="text"
          class="input input-bordered input-sm w-full focus:input-primary"
          value={value}
          placeholder={placeholder}
          onInput={(e) => onChange((e.target as HTMLInputElement).value)}
        />
      </Field>
    );

    const Toggle = (label: string, value: boolean, onChange: (v: boolean) => void) => (
      <label class="label cursor-pointer justify-start gap-3">
        <input
          type="checkbox"
          class="toggle toggle-primary toggle-sm"
          checked={value}
          onChange={(e) => onChange((e.target as HTMLInputElement).checked)}
        />
        <span class="label-text">{label}</span>
      </label>
    );

    const Settings = () => (
      <div class="space-y-3">
        {TextInput(t("campaigns.studio_email.from_name"), props.email.from_name, (v) => props.onEmailChange({ from_name: v }))}
        {TextInput(t("campaigns.studio_email.reply_to"), props.email.reply_to, (v) => props.onEmailChange({ reply_to: v }))}
        <Field
          label={t("campaigns.studio_email.subject")}
          counter={CharCounter(props.email.subject.length, 80)}
          error={props.email.subject.length > 80}
          hint={t("campaigns.studio_email.subject_hint")}
        >
          <TemplateInput
            channel="email"
            value={props.email.subject}
            onChange={(v) => props.onEmailChange({ subject: v })}
          />
        </Field>
        <Field label={t("campaigns.studio_email.preheader")} hint={t("campaigns.studio_email.preheader_hint")}>
          <TemplateInput
            channel="email"
            value={props.email.preheader}
            onChange={(v) => props.onEmailChange({ preheader: v })}
          />
        </Field>
        <div class="rounded-lg bg-base-200 p-3 text-xs text-base-content/60">
          <span class="material-symbols-outlined mr-1 align-middle text-sm">touch_app</span>
          {t("campaigns.studio_email.select_block_hint")}
        </div>
      </div>
    );

    const BlockForm = (block: EmailBlock) => {
      switch (block.type) {
        case "heading":
          return [
            <Field label={t("campaigns.studio_email.text")}>
              <TemplateInput channel="email" value={block.text} onChange={(v) => set(block, { text: v })} />
            </Field>,
            <Field label={t("campaigns.studio_email.size")}>
              <Segmented
                size="xs"
                value={String(block.level)}
                options={[
                  { value: "1", label: "H1" },
                  { value: "2", label: "H2" },
                ]}
                onChange={(v) => set(block, { level: Number(v) as 1 | 2 })}
              />
            </Field>,
            AlignPicker(block.align, (v) => set(block, { align: v })),
          ];
        case "text":
          return [
            <Field label={t("campaigns.studio_email.text")} hint={t("campaigns.studio_email.text_format_hint")}>
              <TemplateTextarea channel="email" rows={7} value={block.text} onChange={(v) => set(block, { text: v })} />
            </Field>,
            AlignPicker(block.align, (v) => set(block, { align: v })),
          ];
        case "image":
          return [
            <Field label={t("campaigns.studio_email.image")}>
              <div class="grid grid-cols-2 gap-2">
                {STOCK_IMAGES.map((src) => (
                  <button
                    key={src}
                    type="button"
                    class={["overflow-hidden rounded border-2", block.src === src ? "border-primary" : "border-transparent"]}
                    onClick={() => set(block, { src })}
                  >
                    <img src={src} class="aspect-[2/1] w-full object-cover" />
                  </button>
                ))}
              </div>
            </Field>,
            <button type="button" class="btn btn-outline btn-sm w-full gap-2" disabled>
              <span class="material-symbols-outlined text-base">upload</span>
              {t("campaigns.studio_email.upload_image")}
            </button>,
            <Field label={t("campaigns.studio_email.width")} counter={`${block.width}%`}>
              <input
                type="range"
                min={30}
                max={100}
                step={5}
                class="range range-primary range-xs"
                value={block.width}
                onInput={(e) => set(block, { width: Number((e.target as HTMLInputElement).value) })}
              />
            </Field>,
            TextInput(t("campaigns.studio_email.alt_text"), block.alt, (v) => set(block, { alt: v })),
            TextInput(t("campaigns.studio_email.link"), block.href, (v) => set(block, { href: v }), "https://"),
          ];
        case "button":
          return [
            <Field label={t("campaigns.studio_email.label")}>
              <TemplateInput channel="email" value={block.label} onChange={(v) => set(block, { label: v })} />
            </Field>,
            TextInput(t("campaigns.studio_email.link"), block.href, (v) => set(block, { href: v }), "https://"),
            AlignPicker(block.align, (v) => set(block, { align: v })),
            Toggle(t("campaigns.studio_email.full_width"), block.full_width, (v) => set(block, { full_width: v })),
          ];
        case "coupon":
          return [
            <Field label={t("campaigns.studio_email.headline")}>
              <TemplateInput channel="email" value={block.headline} onChange={(v) => set(block, { headline: v })} />
            </Field>,
            Toggle(t("campaigns.studio_email.show_qr"), block.show_qr, (v) => set(block, { show_qr: v })),
            Toggle(t("campaigns.studio_email.show_expiry"), block.show_expiry, (v) => set(block, { show_expiry: v })),
            <p class="text-xs text-base-content/50">{t("campaigns.studio_email.coupon_hint")}</p>,
          ];
        case "products":
          return [
            <Field label={t("campaigns.studio_email.products")}>
              <div class="space-y-1">
                {STUB_PRODUCTS.map((p) => (
                  <label key={p.id} class="flex cursor-pointer items-center gap-2 rounded p-1 hover:bg-base-200">
                    <input
                      type="checkbox"
                      class="checkbox checkbox-primary checkbox-xs"
                      checked={block.product_ids.includes(p.id)}
                      onChange={(e) => {
                        const on = (e.target as HTMLInputElement).checked;
                        set(block, {
                          product_ids: on ? [...block.product_ids, p.id] : block.product_ids.filter((id) => id !== p.id),
                        });
                      }}
                    />
                    <img src={p.image} class="h-6 w-6 rounded object-cover" />
                    <span class="text-sm">{p.name}</span>
                  </label>
                ))}
              </div>
            </Field>,
            <Field label={t("campaigns.studio_email.columns")}>
              <Segmented
                size="xs"
                value={String(block.columns)}
                options={[
                  { value: "2", label: "2" },
                  { value: "3", label: "3" },
                ]}
                onChange={(v) => set(block, { columns: Number(v) as 2 | 3 })}
              />
            </Field>,
          ];
        case "spacer":
          return [
            <Field label={t("campaigns.studio_email.height")} counter={`${block.height}px`}>
              <input
                type="range"
                min={8}
                max={96}
                step={4}
                class="range range-primary range-xs"
                value={block.height}
                onInput={(e) => set(block, { height: Number((e.target as HTMLInputElement).value) })}
              />
            </Field>,
          ];
        case "social":
          return [
            TextInput("Instagram", block.instagram, (v) => set(block, { instagram: v }), "@handle"),
            TextInput("Facebook", block.facebook, (v) => set(block, { facebook: v })),
            TextInput(t("campaigns.studio_email.website"), block.website, (v) => set(block, { website: v }), "https://"),
          ];
        case "footer":
          return [
            <Field label={t("campaigns.studio_email.text")} hint={t("campaigns.studio_email.footer_hint")}>
              <TemplateTextarea channel="email" rows={3} value={block.text} onChange={(v) => set(block, { text: v })} />
            </Field>,
          ];
        case "divider":
          return [<p class="text-sm text-base-content/50">{t("campaigns.studio_email.no_settings")}</p>];
      }
    };

    return () => (
      <div class="flex h-full flex-col">
        <div class="flex h-12 shrink-0 items-center justify-between border-b border-base-300 px-3">
          <span class="font-semibold">
            {props.block ? t(blockLabel(props.block.type)) : t("campaigns.studio_email.email_settings")}
          </span>
          {props.block && (
            <button type="button" class="btn btn-ghost btn-xs" onClick={props.onDeselect}>
              <span class="material-symbols-outlined text-base">close</span>
            </button>
          )}
        </div>
        <div class="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
          {props.block ? BlockForm(props.block) : Settings()}
        </div>
      </div>
    );
  },
});
